<template>
	<LoadingComponent :loading="loadingWithNoResources" />

	<Four04Component v-if="!loading && !resource" />

	<div
		v-if="resource"
		class="resource-single-container"
	>
		<AuthorisationComponent :allow-ef="true">
			<a
				target="_blank"
				:href="resource.edit_link"
				class="resource-edit"
				title="Edit resource (>=EF only)"
				>Edit</a
			>
		</AuthorisationComponent>

		<component
			:is="resourceComponent"
			:resource="resource"
		/>
	</div>

	<!-- .print-footer is used by Sejda to ensure the page has loaded: ie the resource is there -->
	<div
		v-if="resource"
		class="print-footer"
	>
		<div class="divider">
			<div class="stacked-logo-container">
				<LogoStackedComponent />
			</div>
		</div>
		<div class="disclaimer">This article is for use by by the Real Schools community. Please do not distribute to any individual without a direct relationship to your school.</div>
	</div>

	<div
		v-if="canAccessPortal && relatedResources.length > 0"
		class="related"
	>
		<div class="divider">
			<div class="logo-container">
				<LogoComponent />
			</div>
		</div>

		<div class="related-list">
			<ResourcesListItemComponent
				:hide-actions="true"
				v-for="r in relatedResources"
				:key="r.ID"
				:resource="r"
			/>
		</div>
	</div>

	<!--
		Shown to anyone who reached this resource by link rather than through the portal. Someone
		who is already logged in gets their own message: telling them to log in is no help, since
		the reason they can't see the rest is that their account isn't connected to a school.
	-->
	<div
		v-if="resource && !canAccessPortal"
		class="find-out-more-container"
	>
		<div class="divider">
			<div class="logo-container">
				<LogoComponent />
			</div>
		</div>

		<div class="find-out-more">
			<p v-if="!isLoggedIn">
				If you're a staff member at a Real Schools partner school, you can
				<router-link
					:to="{
						name: 'login',
					}"
				>
					login here </router-link
				>. To find out more about a Real Schools Partnership,
				<a
					href="https://realschools.com.au/partnerships/"
					target="_blank"
					>click here</a
				>.
			</p>
			<p v-else>
				You're viewing this as <strong>{{ email }}</strong
				>. Your login isn't connected to a school, so you can only see resources you've been sent a link to. If that's not right, please contact us at <a href="mailto:tech@realschools.com.au">tech@realschools.com.au</a>.
			</p>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, watch, type Component } from 'vue';
import { useResourceStore } from '@/stores/resourceStore';
import { mapActions, mapState, setMapStoreSuffix } from 'pinia';
import LogoComponent from '@/images/logo.svg?component';
import LogoStackedComponent from '@/images/logo-stacked-reverse.svg?component';
import AuthorisationComponent from '@/components/shared/AuthorisationComponent.vue';
import ResourceSingleArticleComponent from '@/components/resources/ResourceSingleArticleComponent.vue';
import ResourceSingleVideoComponent from '@/components/resources/ResourceSingleVideoComponent.vue';
import ResourceSingleBotComponent from '@/components/resources/ResourceSingleBotComponent.vue';
import ResourceSingleDefaultComponent from '@/components/resources/ResourceSingleDefaultComponent.vue';
import ResourcesListItemComponent from '@/components/resources/ResourcesListItemComponent.vue';
import RestoringTeachingBookComponent from '@/components/resources/static/RestoringTeachingBookComponent.vue';
import TruthAboutStudentBehaviourBookComponent from '@/components/resources/static/TruthAboutStudentBehaviourBookComponent.vue';
import InstructionAndRelationshipsComponent from '@/components/resources/static/InstructionAndRelationshipsComponent.vue';
import PartnershipHandbooksComponent from '@/components/resources/static/PartnershipHandbooksComponent.vue';
import RealSchoolsOfThoughtComponent from '@/components/resources/static/RealSchoolsOfThoughtComponent.vue';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';
import Four04Component from '@/components/Four04Component.vue';
import { useUser } from '@/stores/userStore';
import { resourceTypeKeys, resourceAudienceKeys } from '@/interfaces/resourceFilterInterface';
import type { Resource } from '@/interfaces/resourceInterface';
setMapStoreSuffix('');

const staticResourceComponentMap = {
	'restoring-teaching-book': RestoringTeachingBookComponent,
	'student-behaviour': TruthAboutStudentBehaviourBookComponent,
	'instruction-and-relationships': InstructionAndRelationshipsComponent,
	'partnership-handbooks': PartnershipHandbooksComponent,
	'real-schools-of-thought': RealSchoolsOfThoughtComponent,
};

export default defineComponent({
	components: {
		LogoComponent,
		ResourcesListItemComponent,
		LoadingComponent,
		AuthorisationComponent,
		LogoStackedComponent,
		Four04Component,
		RestoringTeachingBookComponent,
		TruthAboutStudentBehaviourBookComponent,
		ResourceSingleDefaultComponent,
		ResourceSingleVideoComponent,
		ResourceSingleArticleComponent,
		ResourceSingleBotComponent,
		InstructionAndRelationshipsComponent,
		PartnershipHandbooksComponent,
		RealSchoolsOfThoughtComponent,
	},
	data() {
		return {};
	},
	computed: {
		resourceComponent(): Component | null {
			if (!this.resource) {
				return null;
			}

			if (this.resource.post_name in staticResourceComponentMap) {
				return staticResourceComponentMap[this.resource.post_name as keyof typeof staticResourceComponentMap];
			}

			switch (this.resource.type) {
				case resourceTypeKeys.Video:
					return ResourceSingleVideoComponent;
				case resourceTypeKeys.Article:
					return ResourceSingleArticleComponent;
				case resourceTypeKeys.Bot:
					return ResourceSingleBotComponent;
				default:
					return ResourceSingleDefaultComponent;
			}
		},
		relatedResources(): Resource[] {
			if (!this.resource) {
				return [];
			}

			const currentResourceCategoryTermIds = this.resource.categories.map((t) => t.term_id);
			if (!currentResourceCategoryTermIds) {
				return [];
			}

			// Don't show related resources if this is a parent video
			if (this.resource.type === resourceTypeKeys.Video && this.resource.audience === resourceAudienceKeys.Parent) {
				return [];
			}

			const categoryResources = this.resources.filter((r) => {
				// don't show current resource as related
				if (r.ID === this.resource?.ID) {
					return false;
				}

				if (this.isResourceFavourited(r.ID)) {
					return false;
				}

				return r.categories.map((t) => t.term_id).filter((tId) => currentResourceCategoryTermIds.includes(tId)).length;
			});

			return categoryResources.length ? categoryResources.slice(0, 3) : [];
		},
		resource(): Resource | undefined {
			if (!this.resources) {
				return;
			}
			const slug = this.$route.params.slug as string;

			return this.resources.find((r) => {
				return r.post_name === slug;
			});
		},
		...mapState(useResourceStore, ['resources', 'loading', 'loadingWithNoResources', 'isResourceFavourited']),
		...mapState(useUser, ['isLoggedIn', 'canAccessPortal', 'email']),
	},
	watch: {
		resource(newResource: Resource | undefined, oldResource: Resource | undefined) {
			// If the user viewing this has no access to the portal itself, it means they have
			// gotten here via a link...
			if (this.canAccessPortal) {
				return;
			}

			// then resources should just be the one...
			if (this.resources.length !== 1) {
				return;
			}

			// If there is no new resource they are going to, don't do anything
			if (newResource) {
				return;
			}

			// And if they didn't come from a resource...
			if (!oldResource) {
				return;
			}

			// And if it wasn't a parent video...
			if (oldResource.audience !== resourceAudienceKeys.Parent) {
				return;
			}

			if (oldResource.type !== resourceTypeKeys.Video) {
				return;
			}
			// Then don't so anything.

			// Otherwise...
			// It means a user without portal access has visited a parent video and then clicked on another
			// video in the series. So if that happens, lets reload the resources so they can view it.
			this.loadResources();
		},
	},
	async beforeMount() {
		this.updateDocTitle();

		watch(() => this.resource, this.updateDocTitle);

		// If this user is currently in the onboarding, we need to move their onboarding to
		// the next step
		this.triggerResourceView();
	},
	methods: {
		updateDocTitle() {
			const sitename = 'Parent Portal | Real Schools';
			document.title = this.resource ? `${this.resource.post_title} | ${sitename}` : `Resource | ${sitename}`;
		},
		...mapActions(useResourceStore, ['loadResources']),
		...mapActions(useUser, ['triggerResourceView']),
	},
});
</script>

<style scoped lang="scss">
.resource-single-container {
	position: relative;

	.resource-edit {
		position: absolute;
		top: 20px;
		right: 0;
		z-index: 1;
		border-radius: var(--rs-border-radius);
		background-color: white;
		padding: 6px 8px;
		text-decoration: none;
	}
}

.print-footer {
	display: none;
	margin-top: 50px;
	color: rgb(var(--rs-color-brand-grey-text));
	font-size: 0.5em;

	.divider {
		margin-bottom: 0.7em;

		.stacked-logo-container {
			position: absolute;
			top: -20px;
			right: 0;
			background-color: white;
			padding-left: 20px;
		}

		:deep() {
			svg {
				width: 130px;

				.icon-logo-stacked-text {
					path {
						fill: rgb(var(--rs-color-brand-navy));
					}
				}
			}
		}
	}
}

.divider {
	display: flex;
	position: relative;
	justify-content: center;
	margin: 3rem 0 6rem 0;
	border-bottom: 2px dotted rgb(var(--rs-color-brand-orange));

	.logo-container {
		display: flex;
		justify-content: center;
		transform: translateY(15px);
		background-color: white;
		width: 40px;
		height: 30px;

		svg {
			width: 20px;
			height: auto;
		}
	}
}

.find-out-more {
	margin: -20px 0 40px;
	background-color: rgb(var(--rs-color-brand-tomato));
	padding: 23px 20px 15px 20px;
	color: white;

	a {
		color: rgb(var(--rs-color-brand-mustard));
	}
}

.related {
	margin-bottom: 50px;

	.related-list {
		display: flex;
		flex-direction: column;
		gap: 20px;

		article {
			border-top: 1px solid #cccccc;
			padding-top: 20px;
			font-size: 0.9rem;

			:deep() {
				.favourite {
					display: none;
				}
			}

			&:first-child {
				border-top: 0;
				padding-top: 0;
			}
		}
	}
}

@media screen and (width >= 1024px) {
	.related {
		.related-list {
			flex-direction: row;
			justify-content: space-between;
			gap: 0;

			article {
				flex: 0 33%;
				border-top: 0;
				border-left: 1px solid #cccccc;
				padding: 0 10px;

				&:first-child {
					border-left: 0;
				}
			}
		}
	}
}

@media only print {
	*:deep() {
		-webkit-print-color-adjust: exact !important; /* Chrome, Safari 6 – 15.3, Edge */
		print-color-adjust: exact !important; /* Firefox 97+, Safari 15.4+ */
	}

	.resource-edit {
		display: none;
	}

	.related {
		display: none;
	}

	.find-out-more-container {
		display: none;
	}

	.print-footer {
		display: block;
	}
}
</style>
